import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import useScrollReveal from '../hooks/useScrollReveal';
import { mergeFeaturedFarmers } from '../data/farmers';
import { listPublicFarmerReports } from '../lib/firestore';
import { extractFirstImageFromHtml } from '../lib/articleHtml';
import visibilityBg from '../file/view/optimized/visibility-hero.webp';
import sofixMaterialBg from '../file/sofix/SOFIX material-circulation.png';
import visibilityCardBg from '../file/view/生産の見える化背景画像.jpg';
import Seo from '../components/Seo';

function pickVisibleImageUrl(...candidates) {
  for (const candidate of candidates) {
    const value = String(candidate || '').trim();
    if (!value || value.startsWith('blob:')) continue;
    return value;
  }
  return '';
}

export default function VisibilityPage() {
  const [farmers, setFarmers] = useState([]);
  const [farmerReports, setFarmerReports] = useState([]);
  const [visibleArticleCount, setVisibleArticleCount] = useState(3);
  const [loadingFarmers, setLoadingFarmers] = useState(true);
  const [loadingPosts, setLoadingPosts] = useState(true);

  useScrollReveal();

  useEffect(() => {
    setFarmers(mergeFeaturedFarmers([]));
    setLoadingFarmers(false);

    listPublicFarmerReports()
      .then((reports) => {
        const sorted = [...reports].sort((a, b) =>
          String(b.publishedDate || b.month || '').localeCompare(String(a.publishedDate || a.month || ''))
        );
        setFarmerReports(sorted);
      })
      .finally(() => setLoadingPosts(false));
  }, []);

  const featuredFarmers = useMemo(() => farmers.slice(0, 6), [farmers]);
  const featuredPosts = useMemo(
    () =>
      farmerReports.slice(0, visibleArticleCount),
    [farmerReports, visibleArticleCount]
  );
  const hasMoreFeaturedPosts = useMemo(
    () => farmerReports.length > visibleArticleCount,
    [farmerReports.length, visibleArticleCount]
  );

  return (
    <>
      <Seo
        title="生産の見える化"
        description="参加農家の現場、検査結果、記事を通じて、Drs.Farmがどのように食卓へ届く背景を見える形で伝えているかをまとめたページです。"
        path="/visibility"
        image={visibilityBg}
      />

      <section className="visibility-hero panel reveal" data-reveal style={{ backgroundImage: `linear-gradient(135deg, rgba(246, 246, 242, 0.88), rgba(232, 240, 236, 0.84)), url(${visibilityBg})` }}>
        <div className="visibility-hero-copy">
          <p className="eyebrow">Visibility</p>
          <h1>生産の見える化</h1>
          <p className="visibility-lead">
            どんな人が、どんな考えで育て、どのような状態の土で作物を支えているのか。
            Drs.Farmでは、農家さんの言葉と記事、検査結果をつないで見られるようにしています。
          </p>
        </div>
        <div className="visibility-hero-points">
          <article className="visibility-mini-card">
            <h2>農家さんの現場</h2>
            <p>栽培の哲学や土づくりを、農家さんごとのページで確認できます。</p>
          </article>
          <article className="visibility-mini-card">
            <h2>記事の積み重ね</h2>
            <p>日々の取り組みや季節ごとの変化を、記事として追えるようにしています。</p>
          </article>
          <article className="visibility-mini-card">
            <h2>検査結果の見方</h2>
            <p>検査結果を、一般の方が読みやすいように解説付きで整理しています。</p>
          </article>
        </div>
      </section>

      <section className="panel reveal" data-reveal>
        <header className="section-head visibility-head">
          <div>
            <p className="eyebrow">Farmers</p>
            <h2>農家さんごとの生産背景を見る</h2>
          </div>
          <p className="visibility-copy">各農家さんの詳細ページでは、栽培の哲学、土づくり、検査結果、関連する記事をまとめて確認できます。</p>
        </header>
        {loadingFarmers ? (
          <p>農家データを読み込み中...</p>
        ) : (
          <div className="visibility-farmer-grid">
            {featuredFarmers.map((farmer) => (
              <article key={farmer.id} className="visibility-farmer-card">
                {farmer.heroImageUrl ? (
                  <img src={farmer.heroImageUrl} alt={farmer.name} loading="lazy" className="visibility-farmer-image" />
                ) : null}
                <div className="visibility-farmer-body">
                  <p className="meta-text">{farmer.farmerType === 'parttime' ? 'ライフスタイル農家' : '専業農家'}</p>
                  <h3>{farmer.name}</h3>
                  {farmer.ownerName ? <p className="visibility-owner">{farmer.ownerName}</p> : null}
                  <p>{farmer.description || '生産背景を公開準備中です。'}</p>
                  <Link className="text-btn" to={`/farmers/${farmer.slug}#visibility`}>生産背景を見る</Link>
                </div>
              </article>
            ))}
          </div>
        )}
      </section>

      <section className="panel reveal" data-reveal>
        <header className="section-head visibility-head">
          <div>
            <p className="eyebrow">Articles</p>
            <h2>記事から現場の変化を読む</h2>
          </div>
          <p className="visibility-copy">農家さんの近況報告を時系列で確認できます。</p>
        </header>
        {loadingPosts ? (
          <p>記事を読み込み中...</p>
        ) : featuredPosts.length ? (
          <div className="article-preview-list visibility-article-list">
            {featuredPosts.map((post) => {
              const previewImageUrl = pickVisibleImageUrl(
                post.introImageUrl,
                post.coverImageUrl,
                extractFirstImageFromHtml(post.contentHtml || '')
              );
              return (
                <article key={`report-${post.farmerId}-${post.id}`} className="article-preview-item visibility-article-item">
                  {previewImageUrl ? (
                    <img
                      src={previewImageUrl}
                      alt={post.title || '近況報告の冒頭画像'}
                      loading="lazy"
                      className="visibility-article-thumbnail"
                    />
                  ) : null}
                  <div className="visibility-article-header">
                    <p className="meta-text">{post.farmerName || '農家'} の近況報告</p>
                    {(post.publishedDate || post.month) ? <p className="meta-text">{post.publishedDate || post.month}</p> : null}
                  </div>
                  <h3 className="visibility-article-title">{post.title}</h3>
                  <p className="visibility-article-excerpt">
                    {String(post.body || '').slice(0, 120)}
                    {String(post.body || '').length > 120 ? '...' : ''}
                  </p>
                  <div className="visibility-article-tags">
                    {post.tags?.length ? <p className="meta-text">タグ: {post.tags.join(' / ')}</p> : null}
                    {post.cropTags?.length ? <p className="meta-text">野菜タグ: {post.cropTags.join(' / ')}</p> : null}
                  </div>
                  <Link
                    className="text-btn"
                    to={post.farmerSlug ? `/farmers/${post.farmerSlug}#monthly-report-${post.id}` : '/farmers'}
                  >
                    近況報告を見る
                  </Link>
                </article>
              );
            })}
            <div className="row-actions">
              {hasMoreFeaturedPosts ? (
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setVisibleArticleCount((current) => current + 3)}
                >
                  さらに読む
                </button>
              ) : null}
            </div>
          </div>
        ) : (
          <p>公開されている記事はまだありません。</p>
        )}
      </section>

      <section className="panel reveal" data-reveal>
        <header className="section-head visibility-head">
          <div>
            <h2>検査結果の読み方を知る</h2>
          </div>
          <p className="visibility-copy">SOFIXの原本は専門的です。まずは解説ページで、どこを見ると分かりやすいかを整理しています。</p>
        </header>
        <div className="visibility-sofix-grid">
          <article
            className="info-callout visibility-sofix-card visibility-sofix-card-with-bg"
            style={{ backgroundImage: `linear-gradient(135deg, rgba(14, 27, 23, 0.55), rgba(27, 55, 45, 0.42)), url(${sofixMaterialBg})` }}
          >
            <p className="info-callout-title">SOFIXとは？</p>
            <p>化学肥料や農薬と土の関係を、土の中の循環という視点から理解するためのガイドです。</p>
            <Link className="btn btn-secondary" to="/sofix">SOFIXの説明を見る</Link>
          </article>
          <article
            className="info-callout visibility-sofix-card visibility-sofix-card-with-bg"
            style={{ backgroundImage: `linear-gradient(135deg, rgba(14, 27, 23, 0.58), rgba(27, 55, 45, 0.48)), url(${visibilityCardBg})` }}
          >
            <p className="info-callout-title">農家さんごとの検査結果</p>
            <p>農家詳細ページの「検査結果」タブでは、注目箇所をオーバーレイで追いながら確認できます。</p>
            <Link className="btn btn-secondary" to="/farmers">参加農家を見る</Link>
          </article>
        </div>
      </section>
    </>
  );
}
